import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Heading, Text, LoadingSpinner } from '../ui'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { useAuthContext } from '../../contexts/AuthContext'
import { supportCenterAssistancesService } from '../../services/supportCenterAssistances'
import { supportCenterTriageCatalogService } from '../../services/supportCenterTriageCatalog'
import type { SupportCenterAssistance } from '../../types/supportCenterAssistances'
import type {
  SupportCenterDemandType,
  SupportCenterReferralOrigin,
  SupportCenterTriageCatalogInput,
} from '../../types/supportCenterTriage'
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import { MODULE_NAME } from './parametrosHelpers'

type CatalogSubTab = 'referral' | 'demand'
type CatalogRow = SupportCenterReferralOrigin | SupportCenterDemandType

function getErrorMessage(error: unknown): string {
  const maybe = error as { message?: string; code?: string; details?: string }
  const message = error instanceof Error ? error.message : maybe?.message
  const code = maybe?.code
  const blob = `${message ?? ''} ${maybe?.details ?? ''}`.toLowerCase()
  if (code === '23505' || blob.includes('duplicate key') || blob.includes('_name_active_key')) {
    return 'Já existe uma opção ativa com este nome.'
  }
  if (message) return message
  if (code) return `Erro (${code})`
  return 'Erro inesperado ao processar solicitação.'
}

const EMPTY_FORM: SupportCenterTriageCatalogInput = {
  name: '',
  requires_notes: false,
  assistance_ids: [],
}

const inputClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'

type Props = {
  onCatalogChanged?: () => void
}

export const TriagemOpcoesPanel: React.FC<Props> = ({ onCatalogChanged }) => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')

  const [activeTab, setActiveTab] = React.useState<CatalogSubTab>('referral')
  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)

  const [referralOrigins, setReferralOrigins] = React.useState<SupportCenterReferralOrigin[]>([])
  const [demandTypes, setDemandTypes] = React.useState<SupportCenterDemandType[]>([])
  const [assistances, setAssistances] = React.useState<SupportCenterAssistance[]>([])
  const [filter, setFilter] = React.useState('')

  const [formModal, setFormModal] = React.useState<SupportCenterTriageCatalogInput | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)
  const [submittingForm, setSubmittingForm] = React.useState(false)

  const [deleteModal, setDeleteModal] = React.useState<{ id: string; label: string } | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const [origins, demands, assistanceRows] = await Promise.all([
        supportCenterTriageCatalogService.listReferralOrigins(),
        supportCenterTriageCatalogService.listDemandTypes(),
        supportCenterAssistancesService.list(),
      ])
      setReferralOrigins(origins)
      setDemandTypes(demands)
      setAssistances(assistanceRows)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const rows: CatalogRow[] = activeTab === 'referral' ? referralOrigins : demandTypes
  const filteredRows = rows.filter((row) =>
    row.name.toLocaleLowerCase('pt-BR').includes(filter.trim().toLocaleLowerCase('pt-BR'))
  )

  const tabClass = (tab: CatalogSubTab) =>
    `whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
      activeTab === tab
        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  const refreshAfterChange = async () => {
    await loadData()
    onCatalogChanged?.()
  }

  const openNew = () => {
    setFormError(null)
    setFormModal({
      ...EMPTY_FORM,
      assistance_ids: activeTab === 'demand' ? [] : undefined,
    })
  }

  const openEdit = (row: CatalogRow) => {
    setFormError(null)
    setFormModal({
      id: row.id,
      name: row.name,
      requires_notes: row.requires_notes,
      assistance_ids:
        activeTab === 'demand' && 'assistances' in row
          ? row.assistances.map((item) => item.id)
          : undefined,
    })
  }

  const closeForm = () => {
    if (submittingForm) return
    setFormModal(null)
    setFormError(null)
  }

  const handleSave = async () => {
    if (!formModal || !user?.id) return
    if (
      activeTab === 'demand' &&
      !(formModal.assistance_ids && formModal.assistance_ids.length > 0)
    ) {
      setFormError('Selecione ao menos uma assistência para exibir a demanda.')
      return
    }
    setSubmittingForm(true)
    setFormError(null)
    try {
      const savedId =
        activeTab === 'referral'
          ? await supportCenterTriageCatalogService.upsertReferralOrigin(formModal)
          : await supportCenterTriageCatalogService.upsertDemandType(formModal)
      const entityType =
        activeTab === 'referral' ? 'support_center_referral_origin' : 'support_center_demand_type'
      const label =
        activeTab === 'referral' ? 'origem do encaminhamento' : 'tipo de demanda'
      if (formModal.id) {
        void logUpdate(entityType, formModal.id, `Atualizou ${label}: ${formModal.name.trim()}`)
      } else {
        void logCreate(entityType, savedId, `Criou ${label}: ${formModal.name.trim()}`)
      }
      closeForm()
      await refreshAfterChange()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSubmittingForm(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteModal || !user?.id) return
    setSubmittingDelete(true)
    try {
      if (activeTab === 'referral') {
        await supportCenterTriageCatalogService.softDeleteReferralOrigin(
          deleteModal.id,
          user.id,
          deleteReason
        )
      } else {
        await supportCenterTriageCatalogService.softDeleteDemandType(
          deleteModal.id,
          user.id,
          deleteReason
        )
      }
      const entityType =
        activeTab === 'referral' ? 'support_center_referral_origin' : 'support_center_demand_type'
      const label =
        activeTab === 'referral' ? 'origem do encaminhamento' : 'tipo de demanda'
      void logDelete(
        entityType,
        deleteModal.id,
        `Excluiu ${label}: ${deleteModal.label} — ${deleteReason.trim()}`
      )
      setDeleteModal(null)
      setDeleteReason('')
      await refreshAfterChange()
    } catch (error) {
      setPageError(getErrorMessage(error))
      setDeleteModal(null)
    } finally {
      setSubmittingDelete(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
        {pageError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <Text size="sm">{pageError}</Text>
          </div>
        )}

        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              type="button"
              onClick={() => {
                setActiveTab('referral')
                setFilter('')
              }}
              className={tabClass('referral')}
            >
              Origem do encaminhamento
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('demand')
                setFilter('')
              }}
              className={tabClass('demand')}
            >
              Tipo de demanda
            </button>
          </nav>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
                {activeTab === 'referral'
                  ? 'Origens do encaminhamento'
                  : 'Tipos de demanda percebida'}
              </Heading>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {filteredRows.length}
                {filter.trim() && filteredRows.length !== rows.length
                  ? ` de ${rows.length}`
                  : ''}
              </span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filtrar por nome..."
                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              />
              {canInsert && (
                <Button
                  type="button"
                  variant="solid"
                  onClick={openNew}
                  className="inline-flex items-center gap-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  Nova opção
                </Button>
              )}
            </div>
          </div>

          {filteredRows.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <Text size="sm" color="muted">
                {filter.trim()
                  ? 'Nenhuma opção encontrada com o filtro informado.'
                  : 'Nenhuma opção cadastrada.'}
              </Text>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/40">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Nome
                    </th>
                    {activeTab === 'demand' && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Assistências
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Exige complemento
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredRows.map((row) => (
                    <tr key={row.id}>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                        {row.name}
                      </td>
                      {activeTab === 'demand' && (
                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                          {'assistances' in row && row.assistances.length > 0
                            ? row.assistances
                                .map((assistance) => `${assistance.code} - ${assistance.name}`)
                                .join(', ')
                            : '—'}
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                        {row.requires_notes ? 'Sim' : 'Não'}
                      </td>
                      <td className="px-6 py-4 text-right text-sm">
                        <div className="inline-flex items-center gap-2">
                          {canUpdate && (
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              className="p-1.5 rounded text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                              title="Editar"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => {
                                setDeleteReason('')
                                setDeleteModal({ id: row.id, label: row.name })
                              }}
                              className="p-1.5 rounded text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                              title="Excluir"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {formModal &&
        createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-800 shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                {formModal.id ? 'Editar opção' : 'Nova opção'}
              </Heading>
            </div>
            <div className="px-6 py-4 space-y-3">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                  <Text size="sm">{formError}</Text>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formModal.name}
                  onChange={(e) =>
                    setFormModal((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                  }
                  className={inputClass}
                />
              </div>
              {activeTab === 'demand' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Exibir nas assistências <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    {assistances.map((assistance) => {
                      const checked = (formModal.assistance_ids ?? []).includes(assistance.id)
                      return (
                        <label
                          key={assistance.id}
                          className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setFormModal((prev) => {
                                if (!prev) return prev
                                const current = prev.assistance_ids ?? []
                                return {
                                  ...prev,
                                  assistance_ids: checked
                                    ? current.filter((item) => item !== assistance.id)
                                    : [...current, assistance.id],
                                }
                              })
                            }
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span>{`${assistance.code} - ${assistance.name}`}</span>
                        </label>
                      )
                    })}
                    {assistances.length === 0 && (
                      <Text size="sm" color="muted">
                        Nenhuma assistência ativa cadastrada.
                      </Text>
                    )}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Exige campo de texto complementar? <span className="text-red-500">*</span>
                </label>
                <select
                  value={formModal.requires_notes ? 'sim' : 'nao'}
                  onChange={(e) =>
                    setFormModal((prev) =>
                      prev ? { ...prev, requires_notes: e.target.value === 'sim' } : prev
                    )
                  }
                  className={inputClass}
                >
                  <option value="nao">Não</option>
                  <option value="sim">Sim</option>
                </select>
                <Text size="xs" color="muted" className="mt-1">
                  Use &quot;Sim&quot; para opções como &quot;Outros&quot;, que exigem complemento.
                </Text>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={closeForm} disabled={submittingForm}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="solid"
                onClick={() => void handleSave()}
                disabled={submittingForm}
              >
                {submittingForm ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
        )}

      {deleteModal &&
        createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-800 shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Excluir opção
              </Heading>
            </div>
            <div className="px-6 py-4 space-y-3">
              <Text size="sm" className="text-gray-700 dark:text-gray-300">
                Confirma a exclusão de <strong>{deleteModal.label}</strong>?
              </Text>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Motivo <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className={`${inputClass} resize-none`}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  if (!submittingDelete) {
                    setDeleteModal(null)
                    setDeleteReason('')
                  }
                }}
                disabled={submittingDelete}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="solid"
                onClick={() => void handleDelete()}
                disabled={submittingDelete || !deleteReason.trim()}
                className="bg-red-600 hover:bg-red-700"
              >
                {submittingDelete ? 'Excluindo...' : 'Excluir'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
        )}
    </>
  )
}
